import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, ChevronDown, ChevronRight, Copy,
  Pencil, Save, X, Check, FileText, Database,
} from 'lucide-react';
import { ThemeToggle } from '../../_shared/components';
import {
  useLauncherTemplates,
  useUpdateTemplateRows,
  useWaterfallConfigs,
  useUpdateWaterfallConfigRows,
} from '../hooks/useLauncherTemplates';
import { createTemplate } from '../api-client';
import { CellInspector } from './TemplateCellInspector';
import { useApi } from '../../_shared/hooks';
import {
  conditionGroupSummary,
  type WaterfallRow,
} from './waterfallTypes';
import type { ScorecardTemplate } from './types';
import type { RepairableWaterfallRow } from '../wire-schemas';
import {
  invalidChannelCells,
  parseOpenableRows,
  parseSavableRows,
  scanStoredRows,
} from './template-admin-rows';


// ── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  onEdit,
  onDuplicate,
}: {
  template: ScorecardTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
}) {
  const rows = template.rows ?? [];
  const issues = useMemo(() => scanStoredRows(rows), [rows]);
  const statusCols = useMemo(() => {
    const cols = new Set<string>();
    parseOpenableRows(rows)?.forEach(r => Object.keys(r.cells).forEach(k => cols.add(k)));
    return cols.size;
  }, [rows]);

  const isLaunch = template.source === 'launch';
  const displayName = isLaunch
    ? template.campaign_name || template.name
    : template.name;

  return (
    <div className="bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-xl p-4 flex flex-col gap-3 hover:border-blueberry-400 dark:hover:border-blueberry-500 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50 truncate" title={displayName}>
            {displayName}
          </h3>
          {isLaunch && template.campaign_sfdc_id && (
            <p className="text-[11px] text-nightshade-400 font-mono truncate mt-0.5">
              {template.campaign_sfdc_id}
            </p>
          )}
        </div>
        {issues.length > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 whitespace-nowrap">
            <AlertTriangle size={12} />
            {issues.length} issue{issues.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-nightshade-100 dark:bg-nightshade-700 text-nightshade-600 dark:text-nightshade-300">
          {template.campaign_type}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
          isLaunch
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
            : 'bg-blueberry-100 dark:bg-blueberry-900/30 text-blueberry-700 dark:text-blueberry-300'
        }`}>
          {isLaunch ? 'Launch Snapshot' : 'Template'}
        </span>
      </div>

      <div className="text-xs text-nightshade-500 dark:text-nightshade-400 space-y-0.5">
        <p>{rows.length} row{rows.length !== 1 ? 's' : ''} · {statusCols} status col{statusCols !== 1 ? 's' : ''}</p>
        {template.created_at && (
          <p>{new Date(template.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
        )}
      </div>

      <div className="flex gap-2 mt-auto pt-1">
        <button
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blueberry-50 dark:bg-blueberry-900/30 text-blueberry-700 dark:text-blueberry-300 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/50 transition-colors"
        >
          <Pencil size={12} /> Edit routing
        </button>
        <button
          onClick={onDuplicate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-nightshade-100 dark:bg-nightshade-700 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-200 dark:hover:bg-nightshade-600 transition-colors"
        >
          <Copy size={12} /> Duplicate
        </button>
      </div>
    </div>
  );
}

// ── Row Detail (expandable) ──────────────────────────────────────────────────

function RowDetail({
  row,
  onChange,
}: {
  row: RepairableWaterfallRow;
  onChange: (updated: RepairableWaterfallRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const cells = row.cells ?? {};
  const statusColumns = Object.keys(cells);
  const issues = useMemo(() => invalidChannelCells([row]), [row]);

  const condSummary = conditionGroupSummary(
    row.condition_groups ?? [],
    row.group_logic ?? 'AND',
  );

  return (
    <div className="border border-nightshade-200 dark:border-nightshade-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-nightshade-50 dark:hover:bg-nightshade-800/50 transition-colors"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-sm font-medium text-nightshade-900 dark:text-nightshade-50">
          {row.name || 'Unnamed Row'}
        </span>
        <span className="text-xs text-nightshade-400 ml-1">
          {statusColumns.length} col{statusColumns.length !== 1 ? 's' : ''}
        </span>
        {issues.length > 0 && (
          <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
            <AlertTriangle size={10} />
            {issues.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-nightshade-500 dark:text-nightshade-400">
            <span className="font-medium">Conditions:</span> {condSummary}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {statusColumns.map(col => (
              <CellInspector
                key={col}
                cell={cells[col]}
                statusColumn={col}
                onChange={(updated) => {
                  onChange({
                    ...row,
                    cells: { ...cells, [col]: updated },
                  });
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Template Detail Panel ────────────────────────────────────────────────────

function TemplateDetailPanel({
  title,
  rows: initialRows,
  onSave,
  onClose,
  isSaving,
}: {
  title: string;
  rows: RepairableWaterfallRow[];
  onSave: (rows: WaterfallRow[]) => void;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [editedRows, setEditedRows] = useState<RepairableWaterfallRow[]>(initialRows);
  const [dirty, setDirty] = useState(false);

  const totalIssues = useMemo(() => invalidChannelCells(editedRows).length, [editedRows]);
  // Save only what the server and the reader accept: every cell still invalid blocks it.
  const savable = useMemo(() => parseSavableRows(editedRows), [editedRows]);

  const handleRowChange = (idx: number, updated: RepairableWaterfallRow) => {
    const next = [...editedRows];
    next[idx] = updated;
    setEditedRows(next);
    setDirty(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-8 overflow-y-auto">
      <div className="bg-white dark:bg-nightshade-900 rounded-2xl shadow-2xl border border-nightshade-200 dark:border-nightshade-700 w-full max-w-4xl mx-4 mb-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-nightshade-200 dark:border-nightshade-700">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-nightshade-900 dark:text-nightshade-50 truncate max-w-md" title={title}>
              {title}
            </h2>
            {totalIssues > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                <AlertTriangle size={12} />
                {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                onClick={() => { if (savable.ok) onSave(savable.rows); }}
                disabled={isSaving || !savable.ok}
                title={savable.ok ? undefined : 'Fix the invalid cells before saving'}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blueberry-600 text-white hover:bg-blueberry-700 disabled:opacity-50 transition-colors"
              >
                <Save size={12} />
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-nightshade-100 dark:hover:bg-nightshade-800 text-nightshade-500 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {!savable.ok && savable.issues.length > 0 && (
          <div className="px-6 py-3 border-b border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300" role="alert">
            <p className="font-medium">Save is blocked until these cells are fixed:</p>
            <ul className="mt-1 list-disc pl-4">
              {savable.issues.map(i => (
                <li key={`${i.rowId}:${i.statusColumn}`}>{i.rowName} / {i.statusColumn}: {i.field} &quot;{i.value}&quot;</li>
              ))}
            </ul>
          </div>
        )}

        {/* Rows */}
        <div className="p-6 space-y-3">
          {editedRows.length === 0 ? (
            <p className="text-sm text-nightshade-400 text-center py-8">No waterfall rows configured</p>
          ) : (
            editedRows.map((row, idx) => (
              <RowDetail
                key={row.id ?? idx}
                row={row}
                onChange={updated => handleRowChange(idx, updated)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Waterfall Config Card ────────────────────────────────────────────────────

function WaterfallConfigCard({
  config,
  onEdit,
}: {
  config: {
    campaign_sfdc_id: string;
    launcher_status: string;
    waterfall_rows: readonly unknown[] | null;
    updated_at: string | null;
  };
  onEdit: () => void;
}) {
  const rows = config.waterfall_rows ?? [];
  const issues = useMemo(() => scanStoredRows(rows), [rows]);

  return (
    <div className="bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-xl p-4 flex flex-col gap-3 hover:border-blueberry-400 dark:hover:border-blueberry-500 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-xs font-mono text-nightshade-700 dark:text-nightshade-200 truncate" title={config.campaign_sfdc_id}>
            {config.campaign_sfdc_id}
          </h3>
        </div>
        {issues.length > 0 && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 whitespace-nowrap">
            <AlertTriangle size={12} />
            {issues.length}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
          config.launcher_status === 'launched'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-nightshade-100 dark:bg-nightshade-700 text-nightshade-600 dark:text-nightshade-300'
        }`}>
          {config.launcher_status}
        </span>
      </div>

      <div className="text-xs text-nightshade-500 dark:text-nightshade-400 space-y-0.5">
        <p>{rows.length} row{rows.length !== 1 ? 's' : ''}</p>
        {config.updated_at && (
          <p>{new Date(config.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
        )}
      </div>

      <div className="mt-auto pt-1">
        <button
          onClick={onEdit}
          disabled={rows.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blueberry-50 dark:bg-blueberry-900/30 text-blueberry-700 dark:text-blueberry-300 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/50 disabled:opacity-40 transition-colors"
        >
          <Pencil size={12} /> Inspect
        </button>
      </div>
    </div>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

interface ToastState {
  message: string;
  tone: 'success' | 'error';
}

function Toast({ message, tone, onClose }: ToastState & { onClose: () => void }) {
  const isError = tone === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium shadow-lg animate-in fade-in slide-in-from-bottom-2 ${isError ? 'bg-red-600' : 'bg-green-600'}`}
    >
      {isError ? <AlertTriangle size={14} /> : <Check size={14} />}
      {message}
      <button onClick={onClose} className={`ml-2 p-0.5 rounded transition-colors ${isError ? 'hover:bg-red-500' : 'hover:bg-green-500'}`}>
        <X size={12} />
      </button>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'templates' | 'waterfall';

export function TemplateAdminPage() {
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const [activeTab, setActiveTab] = useState<Tab>('templates');
  const [editingTemplate, setEditingTemplate] = useState<{ template: ScorecardTemplate; rows: RepairableWaterfallRow[] } | null>(null);
  const [editingConfig, setEditingConfig] = useState<{ campaign_sfdc_id: string; rows: RepairableWaterfallRow[] } | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  // Data
  const { templates, isLoading: templatesLoading } = useLauncherTemplates();
  const { configs, isLoading: configsLoading } = useWaterfallConfigs();
  const updateTemplateRows = useUpdateTemplateRows();
  const updateConfigRows = useUpdateWaterfallConfigRows();

  // Corruption summary
  const templateIssueCount = useMemo(
    () => templates.reduce((sum, t) => sum + scanStoredRows(t.rows).length, 0),
    [templates],
  );
  const configIssueCount = useMemo(
    () => configs.reduce((sum, c) => sum + scanStoredRows(c.waterfall_rows).length, 0),
    [configs],
  );

  const showToast = (message: string, tone: ToastState['tone'] = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 3000);
  };

  const openTemplate = (template: ScorecardTemplate) => {
    const rows = parseOpenableRows(template.rows);
    if (!rows) return showToast('These rows are damaged beyond what the editor can open', 'error');
    setEditingTemplate({ template, rows });
  };

  const openConfig = (campaignId: string, rows: readonly unknown[]) => {
    const openable = parseOpenableRows(rows);
    if (!openable) return showToast('These rows are damaged beyond what the editor can open', 'error');
    setEditingConfig({ campaign_sfdc_id: campaignId, rows: openable });
  };

  const handleDuplicate = async (template: ScorecardTemplate) => {
    try {
      await createTemplate(apiFetch, {
        name: `${template.name} (copy)`,
        campaign_type: template.campaign_type,
        rows: template.rows,
        source: 'template',
      });
      showToast('Template duplicated');
    } catch {
      showToast('Failed to duplicate', 'error');
    }
  };

  const handleSaveTemplateRows = (rows: WaterfallRow[]) => {
    if (!editingTemplate) return;
    updateTemplateRows.mutate(
      { id: editingTemplate.template.id, rows },
      {
        onSuccess: () => {
          showToast('Template saved');
          setEditingTemplate(null);
        },
      },
    );
  };

  const handleSaveConfigRows = (rows: WaterfallRow[]) => {
    if (!editingConfig) return;
    updateConfigRows.mutate(
      { campaignId: editingConfig.campaign_sfdc_id, rows },
      {
        onSuccess: () => {
          showToast('Waterfall config saved');
          setEditingConfig(null);
        },
      },
    );
  };

  const tabCls = (tab: Tab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-blueberry-600 text-blueberry-700 dark:text-blueberry-300'
        : 'border-transparent text-nightshade-500 hover:text-nightshade-700 dark:hover:text-nightshade-300'
    }`;

  return (
    <div className="min-h-full bg-nightshade-50 dark:bg-nightshade-950 text-nightshade-900 dark:text-nightshade-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 dark:bg-nightshade-900/90 backdrop-blur-sm border-b border-nightshade-200 dark:border-nightshade-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/launcher')}
              className="p-1.5 rounded-lg hover:bg-nightshade-100 dark:hover:bg-nightshade-800 text-nightshade-500 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-nightshade-400">ADMIN</p>
              <h1 className="text-base font-semibold">Template & Routing Configuration</h1>
            </div>
          </div>
          <ThemeToggle />
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          <button className={tabCls('templates')} onClick={() => setActiveTab('templates')}>
            <span className="flex items-center gap-1.5">
              <FileText size={14} />
              Templates
              {templateIssueCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                  {templateIssueCount}
                </span>
              )}
            </span>
          </button>
          <button className={tabCls('waterfall')} onClick={() => setActiveTab('waterfall')}>
            <span className="flex items-center gap-1.5">
              <Database size={14} />
              Waterfall Config
              {configIssueCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                  {configIssueCount}
                </span>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'templates' && (
          <>
            {templatesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-xl p-4 h-44 animate-pulse">
                    <div className="h-4 w-2/3 bg-nightshade-200 dark:bg-nightshade-700 rounded mb-3" />
                    <div className="h-3 w-1/3 bg-nightshade-100 dark:bg-nightshade-800 rounded mb-2" />
                    <div className="h-3 w-1/2 bg-nightshade-100 dark:bg-nightshade-800 rounded" />
                  </div>
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-16">
                <FileText size={40} className="mx-auto text-nightshade-300 dark:text-nightshade-600 mb-3" />
                <p className="text-sm text-nightshade-500">No templates found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(t => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    onEdit={() => openTemplate(t)}
                    onDuplicate={() => void handleDuplicate(t)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'waterfall' && (
          <>
            {configsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-xl p-4 h-36 animate-pulse">
                    <div className="h-3 w-3/4 bg-nightshade-200 dark:bg-nightshade-700 rounded mb-3" />
                    <div className="h-3 w-1/3 bg-nightshade-100 dark:bg-nightshade-800 rounded" />
                  </div>
                ))}
              </div>
            ) : configs.length === 0 ? (
              <div className="text-center py-16">
                <Database size={40} className="mx-auto text-nightshade-300 dark:text-nightshade-600 mb-3" />
                <p className="text-sm text-nightshade-500">No waterfall configs found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {configs.map(c => (
                  <WaterfallConfigCard
                    key={c.campaign_sfdc_id}
                    config={c}
                    onEdit={() => openConfig(c.campaign_sfdc_id, c.waterfall_rows ?? [])}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail panels */}
      {editingTemplate && (
        <TemplateDetailPanel
          title={editingTemplate.template.source === 'launch'
            ? editingTemplate.template.campaign_name || editingTemplate.template.name
            : editingTemplate.template.name}
          rows={editingTemplate.rows}
          onSave={handleSaveTemplateRows}
          onClose={() => setEditingTemplate(null)}
          isSaving={updateTemplateRows.isPending}
        />
      )}

      {editingConfig && (
        <TemplateDetailPanel
          title={editingConfig.campaign_sfdc_id}
          rows={editingConfig.rows}
          onSave={handleSaveConfigRows}
          onClose={() => setEditingConfig(null)}
          isSaving={updateConfigRows.isPending}
        />
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
